import { useState, useEffect, useRef, FormEvent } from 'react';
import { useRouter } from 'next/router';
import { isMobile, validateEmail } from '../../../utils/utils';
import { useDispatch } from 'react-redux';
import { login } from '../../../redux/user/userSlice';
import type { TurnstileHandle } from '../../../components/Turnstile';

// Sólo permite redirigir a una ruta relativa propia (evita open-redirect si
// alguien arma un link a /ingresar?r=<base64 de una URL externa>).
const isSafeRedirect = (url: string) => url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');

type FeedbackType = 'error' | 'success' | 'info';

const useHook = () => {

  const dispatch = useDispatch();
  const Router = useRouter();

  const [mobile, setMobile] = useState(true);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('error');
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef<TurnstileHandle>(null);
  const handleTurnstileVerify = (token: string) => setTurnstileToken(token);
  const handleTurnstileExpire = () => setTurnstileToken("");

  const handleRecuperarCuenta = () => {
    if (!turnstileToken) return;
    const valido = validateEmail(email);
    if (valido) {
      sendRecoveryEmail();
    } else {
      setFeedbackType('error');
      setFeedbackMessage("Para recuperar tu password, ingresá un email válido.");
    }
  };

  const sendRecoveryEmail = async () => {
    const body = { email, turnstileToken };
    try {
      await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/usuarios/send-recovery-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      // El backend responde igual exista o no el email (evita enumeración de usuarios).
      setFeedbackType('success');
      setFeedbackMessage(`Si ${email} está registrado, te enviamos un correo para recuperar tu contraseña.`);
      setEmail("");
      setPassword("");
    } catch (err) {
      console.log(err);
      setFeedbackType('error');
      setFeedbackMessage('Ocurrió un error inesperado. Intentalo nuevamente.');
    } finally {
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }
  };

  const handleEmail = (val: string) => {
    const valido = validateEmail(val);
    setEmail(val.toLowerCase());
    setFeedbackEmail(valido ? "" : "Ingresá un email válido");
  };

  const handlePassword = (val: string) => {
    setPassword(val);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const body = {
      identifier: email,
      password: password
    };

    try {
      const result = await fetch(`${process.env.NEXT_PUBLIC_URL_FRONTEND}/api/usuarios/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await result.json();
      if (data?.jwt) {
        delete data.jwt;
        dispatch(login(data));

        let redirect = "";
        const { r } = Router.query;
        if (r) {
          const decoded = Buffer.from(r as string, 'base64').toString('ascii');
          redirect = isSafeRedirect(decoded) ? decoded : "";
        }

        Router.push(redirect || "/");

      } else {
        setFeedbackType('error');
        setFeedbackMessage('Email o contraseña inválida.');
      }
    } catch (err) {
      console.log(err);
      setFeedbackType('error');
      setFeedbackMessage('Ocurrió un error inesperado. Intentalo nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setMobile(isMobile());
  }, []);

  return {
    mobile,
    loading,
    handleSubmit,
    email,
    handleEmail,
    feedbackEmail,
    password,
    handlePassword,
    showPassword,
    setShowPassword,
    feedbackType,
    feedbackMessage,
    handleRecuperarCuenta,
    turnstileRef,
    handleTurnstileVerify,
    handleTurnstileExpire,
    turnstileToken,
  };
};

export default useHook;
